import {
  type MutableRefObject,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react'
import type { CrmIntegrationMessageDisplay } from '../types/crm-integration-message.types'
import type { CrmIntegrationMessagesLoadStatus } from './useCrmIntegrationMessagesLoad'

const SCROLL_NEAR_BOTTOM_THRESHOLD_PX = 48

const isNearBottom = (viewport: HTMLDivElement): boolean => {
  const distanceToBottom =
    viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight
  return distanceToBottom <= SCROLL_NEAR_BOTTOM_THRESHOLD_PX
}

const scrollViewportToBottom = (viewport: HTMLDivElement): void => {
  viewport.scrollTop = viewport.scrollHeight
}

const scheduleScrollViewportToBottom = (viewport: HTMLDivElement): void => {
  scrollViewportToBottom(viewport)
  requestAnimationFrame(() => {
    scrollViewportToBottom(viewport)
  })
  window.setTimeout(() => scrollViewportToBottom(viewport), 50)
  window.setTimeout(() => scrollViewportToBottom(viewport), 150)
}

const getMessagesTailKey = (items: CrmIntegrationMessageDisplay[]): string | null => {
  const last = items[items.length - 1]
  if (!last) {
    return null
  }
  return String(last.clientId ?? last.id)
}

export type UseCrmIntegrationMessagesScrollParams = {
  conversationId: number | null
  enabled?: boolean
  status: CrmIntegrationMessagesLoadStatus
  messages: CrmIntegrationMessageDisplay[]
  messagesViewportRef: MutableRefObject<HTMLDivElement | null>
}

export type UseCrmIntegrationMessagesScrollResult = {
  messagesListRef: MutableRefObject<HTMLUListElement | null>
  handleMessagesViewportScroll: () => void
  enableStickToBottom: () => void
  scrollToBottom: () => void
}

export const useCrmIntegrationMessagesScroll = ({
  conversationId,
  enabled = true,
  status,
  messages,
  messagesViewportRef,
}: UseCrmIntegrationMessagesScrollParams): UseCrmIntegrationMessagesScrollResult => {
  const messagesListRef: MutableRefObject<HTMLUListElement | null> = useRef(null)
  const stickToBottomRef = useRef(true)
  const pendingOpenScrollRef = useRef(true)
  const isAutoScrollingRef = useRef(false)
  const prevMessagesTailKeyRef = useRef<string | null>(null)

  const pinScrollToBottom = (viewport: HTMLDivElement) => {
    isAutoScrollingRef.current = true
    scheduleScrollViewportToBottom(viewport)
    window.setTimeout(() => {
      isAutoScrollingRef.current = false
    }, 200)
  }

  const enableStickToBottom = () => {
    stickToBottomRef.current = true
  }

  const scrollToBottom = () => {
    const viewport = messagesViewportRef.current
    if (viewport) {
      pinScrollToBottom(viewport)
    }
  }

  const handleMessagesViewportScroll = () => {
    if (isAutoScrollingRef.current) {
      return
    }
    const viewport = messagesViewportRef.current
    if (!viewport) {
      return
    }
    stickToBottomRef.current = isNearBottom(viewport)
  }

  useLayoutEffect(() => {
    if (!enabled) {
      return
    }
    stickToBottomRef.current = true
    pendingOpenScrollRef.current = true
    prevMessagesTailKeyRef.current = null
  }, [conversationId, enabled])

  useLayoutEffect(() => {
    const viewport = messagesViewportRef.current
    if (!viewport || status !== 'ready' || messages.length === 0) {
      return
    }

    const tailKey = getMessagesTailKey(messages)
    const tailChanged =
      prevMessagesTailKeyRef.current !== null &&
      tailKey !== null &&
      tailKey !== prevMessagesTailKeyRef.current
    prevMessagesTailKeyRef.current = tailKey

    if (pendingOpenScrollRef.current) {
      pinScrollToBottom(viewport)
      return
    }

    if (stickToBottomRef.current && tailChanged) {
      pinScrollToBottom(viewport)
    }
  }, [messages, messagesViewportRef, status])

  useEffect(() => {
    if (
      !enabled ||
      !pendingOpenScrollRef.current ||
      status !== 'ready' ||
      messages.length === 0
    ) {
      return
    }

    const viewport = messagesViewportRef.current
    const list = messagesListRef.current
    if (!viewport) {
      return
    }

    const scroll = () => scrollViewportToBottom(viewport)

    scroll()
    const t1 = window.setTimeout(scroll, 100)
    const t2 = window.setTimeout(scroll, 300)

    let resizeObserver: ResizeObserver | null = null
    if (list) {
      resizeObserver = new ResizeObserver(() => {
        if (pendingOpenScrollRef.current) {
          scroll()
        }
      })
      resizeObserver.observe(list)
    }

    const finishId = window.setTimeout(() => {
      pendingOpenScrollRef.current = false
      resizeObserver?.disconnect()
    }, 600)

    return () => {
      window.clearTimeout(t1)
      window.clearTimeout(t2)
      window.clearTimeout(finishId)
      resizeObserver?.disconnect()
    }
  }, [conversationId, enabled, messages, status])

  return {
    messagesListRef,
    handleMessagesViewportScroll,
    enableStickToBottom,
    scrollToBottom,
  }
}
